import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config';
import { 
  GlobalCustomerPermissions, 
  CustomerPermissions, 
  UserPermissionOverride,
  OrganizationPermissionOverride,
  DEFAULT_CUSTOMER_PERMISSIONS 
} from '@/types/permissions';
import { handleFirebaseError } from '../error-handling';
import { getUserOrganizations } from './organizations';
import {
  mergeWithDefaults,
  resolveCustomerPermissionsAcrossOrganizations,
  resolvePersonalCustomerPermissions,
  resolveCustomerPermissionsForOrganization,
} from '@/lib/permissions/customerPermissions';

/**
 * CUSTOMER PERMISSIONS HIERARCHY
 * 
 * The permission system follows a hierarchical structure with specific override rules:
 * 
 * 1. PERSONAL ACCESS (Highest Priority)
 *    - User-specific permission overrides
 *    - Personal "ON" overrides any General or Organizational "OFF"
 *    - Personal "OFF" overrides any General or Organizational "ON"
 *    - Stored in globalPermissions.userOverrides
 * 
 * 2. GENERAL SETTINGS (Medium Priority)
 *    - Global default permissions for all customers
 *    - General "OFF" overrides any Organizational "ON"
 *    - General "ON" can be overridden by Organizational "OFF"
 *    - Stored in globalPermissions.defaultPermissions
 * 
 * 3. ORGANIZATIONAL ACCESS (Lowest Priority)
 *    - Organization-specific permission overrides
 *    - Applied when a user belongs to an organization with custom permissions
 *    - Stored in globalPermissions.organizationOverrides
 *    - When user belongs to multiple organizations, uses the most recent override
 * 
 * OVERRIDE RULES:
 * - General "OFF" → overrides any Organizational "ON"
 * - Personal "ON" → overrides any General or Organizational "OFF"
 * - Personal "OFF" → overrides any General or Organizational "ON"
 * 
 * USAGE:
 * - Use getUserEffectivePermissions(userId) for general permission checks
 * - Use getUserEffectivePermissionsInOrganization(userId, organizationId) for 
 *   organization-specific context (when users can switch between organizations)
 * 
 * EXAMPLES:
 * 
 * Example 1: General OFF overrides Organizational ON
 * - Global: habitTracking = false (blocked for all)
 * - Organization A: habitTracking = true (enabled for org A)
 * - User John: no personal override
 * 
 * Result: John's habitTracking = false (General OFF wins)
 * 
 * Example 2: Personal ON overrides General OFF
 * - Global: habitTracking = false (blocked for all)
 * - Organization A: habitTracking = false (blocked for org A)
 * - User John: habitTracking = true (enabled specifically for John)
 * 
 * Result: John's habitTracking = true (Personal ON wins)
 * 
 * Example 3: Personal OFF overrides Organizational ON
 * - Global: habitTracking = true (enabled for all)
 * - Organization A: habitTracking = true (enabled for org A)
 * - User John: habitTracking = false (blocked specifically for John)
 * 
 * Result: John's habitTracking = false (Personal OFF wins)
 */

// Collection name for global permissions
const GLOBAL_PERMISSIONS_COLLECTION = 'globalCustomerPermissions';
const GLOBAL_PERMISSIONS_DOC_ID = 'default';

/**
 * Get global customer permissions
 */
// One read shared by every caller for a short while: the route guard, the
// navigation and the pages each resolved permissions from their own read of
// this document on every page open. Writes below clear it; a failed read is
// never kept.
const GLOBAL_PERMISSIONS_TTL_MS = 30_000;
let globalPermissionsCache: { at: number; value: Promise<GlobalCustomerPermissions> } | null = null;

export function invalidateGlobalCustomerPermissionsCache(): void {
  globalPermissionsCache = null;
}

export async function getGlobalCustomerPermissions(): Promise<GlobalCustomerPermissions> {
  const now = Date.now();
  if (globalPermissionsCache && now - globalPermissionsCache.at < GLOBAL_PERMISSIONS_TTL_MS) {
    return globalPermissionsCache.value;
  }
  const entry = { at: now, value: readGlobalCustomerPermissions() };
  globalPermissionsCache = entry;
  try {
    return await entry.value;
  } catch (error) {
    if (globalPermissionsCache === entry) globalPermissionsCache = null;
    console.error('Error getting global customer permissions:', error);
    handleFirebaseError(error);
    // Return default permissions as fallback
    return {
      id: GLOBAL_PERMISSIONS_DOC_ID,
      defaultPermissions: mergeWithDefaults(DEFAULT_CUSTOMER_PERMISSIONS),
      userOverrides: [],
      organizationOverrides: [],
      updatedAt: new Date(),
      updatedBy: 'system',
    };
  }
}

/** For read-modify-write of the document: never a cached copy. */
async function getFreshGlobalCustomerPermissions(): Promise<GlobalCustomerPermissions> {
  invalidateGlobalCustomerPermissionsCache();
  return getGlobalCustomerPermissions();
}

async function readGlobalCustomerPermissions(): Promise<GlobalCustomerPermissions> {
  {
    const docRef = doc(db, GLOBAL_PERMISSIONS_COLLECTION, GLOBAL_PERMISSIONS_DOC_ID);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Merge existing permissions with defaults to ensure new permissions are included
      const mergedPermissions: CustomerPermissions = {
        sections: {
          ...DEFAULT_CUSTOMER_PERMISSIONS.sections,
          ...data.defaultPermissions.sections,
        },
        actions: {
          ...DEFAULT_CUSTOMER_PERMISSIONS.actions,
          ...data.defaultPermissions.actions,
        },
      };
    
    return {
        id: docSnap.id,
        defaultPermissions: mergedPermissions,
        userOverrides: data.userOverrides || [],
        organizationOverrides: data.organizationOverrides || [],
      updatedAt: data.updatedAt.toDate(),
        updatedBy: data.updatedBy,
      };
    } else {
      // Create default permissions if they don't exist
      const defaultGlobalPermissions: GlobalCustomerPermissions = {
        id: GLOBAL_PERMISSIONS_DOC_ID,
        defaultPermissions: mergeWithDefaults(DEFAULT_CUSTOMER_PERMISSIONS),
        userOverrides: [],
        organizationOverrides: [],
        updatedAt: new Date(),
        updatedBy: 'system',
      };
      
      await setDoc(docRef, {
        defaultPermissions: defaultGlobalPermissions.defaultPermissions,
        userOverrides: defaultGlobalPermissions.userOverrides,
        organizationOverrides: defaultGlobalPermissions.organizationOverrides,
        updatedAt: Timestamp.now(),
        updatedBy: defaultGlobalPermissions.updatedBy,
      });
      
      return defaultGlobalPermissions;
    }
  }
}

/**
 * Update global default permissions for all customers
 */
export async function updateGlobalCustomerPermissions(
  permissions: CustomerPermissions,
  adminUserId: string
): Promise<void> {
  try {
    const docRef = doc(db, GLOBAL_PERMISSIONS_COLLECTION, GLOBAL_PERMISSIONS_DOC_ID);
    
    // Get existing data to preserve user overrides
    const existing = await getFreshGlobalCustomerPermissions();
    
      await updateDoc(docRef, {
      defaultPermissions: permissions,
      userOverrides: existing.userOverrides,
      organizationOverrides: existing.organizationOverrides,
        updatedAt: Timestamp.now(),
      updatedBy: adminUserId,
    });
    invalidateGlobalCustomerPermissionsCache();
    
    console.log('Global customer permissions updated successfully');
  } catch (error) {
    console.error('Error updating global customer permissions:', error);
    handleFirebaseError(error, 'Failed to update global permissions');
  }
}

/**
 * Set permissions for a specific user (override global permissions)
 */
export async function setUserPermissionOverride(
  userId: string,
  permissions: CustomerPermissions,
  adminUserId: string
): Promise<void> {
  try {
    const docRef = doc(db, GLOBAL_PERMISSIONS_COLLECTION, GLOBAL_PERMISSIONS_DOC_ID);
    const existing = await getFreshGlobalCustomerPermissions();
    
    // Remove existing override for this user if it exists
    const filteredOverrides = existing.userOverrides.filter(override => override.userId !== userId);
    
    // Add new override
    const newOverride: UserPermissionOverride = {
      userId,
      permissions,
      overriddenAt: new Date(),
      overriddenBy: adminUserId,
    };
    
    const updatedOverrides = [...filteredOverrides, newOverride];
    
    await updateDoc(docRef, {
      userOverrides: updatedOverrides.map(override => ({
        ...override,
        overriddenAt: Timestamp.fromDate(override.overriddenAt),
      })),
      organizationOverrides: existing.organizationOverrides.map(override => ({
        ...override,
        overriddenAt: Timestamp.fromDate(override.overriddenAt),
      })),
      updatedAt: Timestamp.now(),
      updatedBy: adminUserId,
    });
    invalidateGlobalCustomerPermissionsCache();
    
    console.log(`User permission override set for user: ${userId}`);
  } catch (error) {
    console.error('Error setting user permission override:', error);
    handleFirebaseError(error, 'Failed to set user permissions');
  }
}

/**
 * Remove permission override for a specific user (revert to global permissions)
 */
export async function removeUserPermissionOverride(
  userId: string,
  adminUserId: string
): Promise<void> {
  try {
    const docRef = doc(db, GLOBAL_PERMISSIONS_COLLECTION, GLOBAL_PERMISSIONS_DOC_ID);
    const existing = await getFreshGlobalCustomerPermissions();
    
    // Remove override for this user
    const filteredOverrides = existing.userOverrides.filter(override => override.userId !== userId);
    
      await updateDoc(docRef, {
      userOverrides: filteredOverrides.map(override => ({
        ...override,
        overriddenAt: Timestamp.fromDate(override.overriddenAt),
      })),
      organizationOverrides: existing.organizationOverrides.map(override => ({
        ...override,
        overriddenAt: Timestamp.fromDate(override.overriddenAt),
      })),
        updatedAt: Timestamp.now(),
      updatedBy: adminUserId,
    });
    invalidateGlobalCustomerPermissionsCache();
    
    console.log(`User permission override removed for user: ${userId}`);
  } catch (error) {
    console.error('Error removing user permission override:', error);
    handleFirebaseError(error, 'Failed to remove user permissions');
  }
}

/**
 * Set permissions for a specific organization (override global permissions)
 */
export async function setOrganizationPermissionOverride(
  organizationId: string,
  organizationName: string,
  permissions: CustomerPermissions,
  adminUserId: string
): Promise<void> {
  try {
    const docRef = doc(db, GLOBAL_PERMISSIONS_COLLECTION, GLOBAL_PERMISSIONS_DOC_ID);
    const existing = await getFreshGlobalCustomerPermissions();
    
    // Remove existing override for this organization if it exists
    const filteredOverrides = existing.organizationOverrides.filter(override => override.organizationId !== organizationId);
    
    // Add new override
    const newOverride: OrganizationPermissionOverride = {
      organizationId,
      organizationName,
      permissions,
      overriddenAt: new Date(),
      overriddenBy: adminUserId,
    };
    
    const updatedOverrides = [...filteredOverrides, newOverride];
    
    await updateDoc(docRef, {
      userOverrides: existing.userOverrides.map(override => ({
        ...override,
        overriddenAt: Timestamp.fromDate(override.overriddenAt),
      })),
      organizationOverrides: updatedOverrides.map(override => ({
        ...override,
        overriddenAt: Timestamp.fromDate(override.overriddenAt),
      })),
        updatedAt: Timestamp.now(),
      updatedBy: adminUserId,
    });
    invalidateGlobalCustomerPermissionsCache();
    
    console.log(`Organization permission override set for organization: ${organizationId}`);
  } catch (error) {
    console.error('Error setting organization permission override:', error);
    handleFirebaseError(error, 'Failed to set organization permissions');
  }
}

/**
 * Remove permission override for a specific organization (revert to global permissions)
 */
export async function removeOrganizationPermissionOverride(
  organizationId: string,
  adminUserId: string
): Promise<void> {
  try {
    const docRef = doc(db, GLOBAL_PERMISSIONS_COLLECTION, GLOBAL_PERMISSIONS_DOC_ID);
    const existing = await getFreshGlobalCustomerPermissions();
    
    // Remove override for this organization
    const filteredOverrides = existing.organizationOverrides.filter(override => override.organizationId !== organizationId);
    
    await updateDoc(docRef, {
      userOverrides: existing.userOverrides.map(override => ({
        ...override,
        overriddenAt: Timestamp.fromDate(override.overriddenAt),
      })),
      organizationOverrides: filteredOverrides.map(override => ({
        ...override,
        overriddenAt: Timestamp.fromDate(override.overriddenAt),
      })),
      updatedAt: Timestamp.now(),
      updatedBy: adminUserId,
    });
    invalidateGlobalCustomerPermissionsCache();
    
    console.log(`Organization permission override removed for organization: ${organizationId}`);
  } catch (error) {
    console.error('Error removing organization permission override:', error);
    handleFirebaseError(error, 'Failed to remove organization permissions');
  }
}

/**
 * Get effective permissions for a specific user
 * Priority: User override > Most recent organization override > Global default
 */
export async function getUserEffectivePermissions(userId: string): Promise<CustomerPermissions> {
  try {
    const globalPermissions = await getGlobalCustomerPermissions();
    const userOrganizations = await getUserOrganizations(userId);
    return resolveCustomerPermissionsAcrossOrganizations(
      globalPermissions,
      userId,
      userOrganizations.map(org => org.id)
    );
  } catch (error) {
    console.error('Error getting user effective permissions:', error);
    handleFirebaseError(error);
    // Return default permissions as fallback
    return mergeWithDefaults(DEFAULT_CUSTOMER_PERMISSIONS);
  }
}

/**
 * Permissions for personal (organization-free) features: platform default +
 * the user's own override; organization overrides do not apply.
 */
export async function getUserPersonalPermissions(userId: string): Promise<CustomerPermissions> {
  try {
    const globalPermissions = await getGlobalCustomerPermissions();
    return resolvePersonalCustomerPermissions(globalPermissions, userId);
  } catch (error) {
    console.error('Error getting user personal permissions:', error);
    return mergeWithDefaults(DEFAULT_CUSTOMER_PERMISSIONS);
  }
}

/**
 * Check if a user has permission for a specific section
 */
export async function userHasSectionPermission(
  userId: string, 
  section: keyof CustomerPermissions['sections']
): Promise<boolean> {
  try {
    const permissions = await getUserEffectivePermissions(userId);
    return permissions.sections[section];
  } catch (error) {
    console.error('Error checking user section permission:', error);
    return true; // Default to allowing access on error
  }
}

/**
 * Check if a user has permission for a specific action
 */
export async function userHasActionPermission(
  userId: string, 
  action: keyof CustomerPermissions['actions']
): Promise<boolean> {
  try {
    const permissions = await getUserEffectivePermissions(userId);
    return permissions.actions[action];
  } catch (error) {
    console.error('Error checking user action permission:', error);
    return true; // Default to allowing access on error
  }
}

/**
 * Get all users with permission overrides
 */
export async function getUsersWithPermissionOverrides(): Promise<UserPermissionOverride[]> {
  try {
    const globalPermissions = await getGlobalCustomerPermissions();
    return globalPermissions.userOverrides;
  } catch (error) {
    console.error('Error getting users with permission overrides:', error);
    handleFirebaseError(error);
    return [];
  }
}

/**
 * Get effective permissions for a user in a specific organization context
 * This is used when users can switch between organizations in their dashboard
 */
export async function getUserEffectivePermissionsInOrganization(
  userId: string, 
  organizationId: string
): Promise<CustomerPermissions> {
  try {
    const globalPermissions = await getGlobalCustomerPermissions();
    return resolveCustomerPermissionsForOrganization(globalPermissions, userId, organizationId);
  } catch (error) {
    console.error('Error getting user effective permissions in organization:', error);
    handleFirebaseError(error);
    // Return default permissions as fallback
    return mergeWithDefaults(DEFAULT_CUSTOMER_PERMISSIONS);
  }
}

/**
 * Get all organizations with permission overrides
 */
export async function getOrganizationsWithPermissionOverrides(): Promise<OrganizationPermissionOverride[]> {
  try {
    const globalPermissions = await getGlobalCustomerPermissions();
    return globalPermissions.organizationOverrides;
  } catch (error) {
    console.error('Error getting organizations with permission overrides:', error);
    handleFirebaseError(error);
    return [];
  }
}

/**
 * Bulk update permissions for multiple users
 */
export async function bulkUpdateUserPermissions(
  userPermissions: { userId: string; permissions: CustomerPermissions }[],
  adminUserId: string
): Promise<void> {
  try {
    const docRef = doc(db, GLOBAL_PERMISSIONS_COLLECTION, GLOBAL_PERMISSIONS_DOC_ID);
    const existing = await getFreshGlobalCustomerPermissions();
    
    // Create map of existing overrides
    const existingOverridesMap = new Map(
      existing.userOverrides.map(override => [override.userId, override])
    );
    
    // Update or add new overrides
    userPermissions.forEach(({ userId, permissions }) => {
      existingOverridesMap.set(userId, {
        userId,
        permissions,
        overriddenAt: new Date(),
        overriddenBy: adminUserId,
      });
    });
    
    const updatedOverrides = Array.from(existingOverridesMap.values());
    
    await updateDoc(docRef, {
      userOverrides: updatedOverrides.map(override => ({
        ...override,
        overriddenAt: Timestamp.fromDate(override.overriddenAt),
      })),
      organizationOverrides: existing.organizationOverrides.map(override => ({
        ...override,
        overriddenAt: Timestamp.fromDate(override.overriddenAt),
      })),
      updatedAt: Timestamp.now(),
      updatedBy: adminUserId,
    });
    invalidateGlobalCustomerPermissionsCache();
    
    console.log(`Bulk updated permissions for ${userPermissions.length} users`);
  } catch (error) {
    console.error('Error bulk updating user permissions:', error);
    handleFirebaseError(error, 'Failed to bulk update user permissions');
  }
}